import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faHouse,
	faRulerVertical,
	faPersonSwimming,
	faWarehouse,
	faElevator,
	faSolarPanel,
	faMountainSun,
	faRoad,
	faShieldHalved
} from '@fortawesome/free-solid-svg-icons';
import styles from './Bistritsa.module.css';
import Contact from '../ui/Contact.js';

import birdseye from '../assets/webp/birdseye.webp';
import air1 from '../assets/webp/Aerial_1_07.2.webp';

function Bistritsa({ languageEn }) {
	const text = {
		en: {
			title: 'Houses for Sale in Bistritsa, Sofia — Vkushty Gated Community',
			metaDescription:
				'New houses for sale in Bistritsa, Sofia: a gated community of just 4 houses on Izgled Street. 640-780 sq.m, 3.39 m ceilings, heated pool, 4-car garage, panoramic views over Sofia.',
			h1: 'Houses in Bistritsa, Sofia',
			subtitle: 'A boutique gated community of just four houses on Izgled Street',
			intro:
				'If you are looking for a house in Bistritsa, Vkushty is the newest gated community in the village: four contemporary houses of 640-780 sq.m on the top of Izgled Street, at 897 m altitude with a 360° panorama over Sofia. New construction with suspended glass facades by Schüco, each house on its own 920-980 sq.m plot.',
			heroAlt: 'Panoramic view over Sofia from the Vkushty houses in Bistritsa',
			factsTitle: 'The houses at a glance',
			facts: [
				{ icon: faHouse, text: 'Only 4 houses, 640-780 sq.m on three levels' },
				{ icon: faRulerVertical, text: '3.39 m clear ceiling height' },
				{ icon: faPersonSwimming, text: 'Heated pool in every house' },
				{ icon: faWarehouse, text: '4-car garage' },
				{ icon: faElevator, text: 'KONE elevator' },
				{ icon: faSolarPanel, text: 'Photovoltaics and heat pumps' },
				{ icon: faShieldHalved, text: 'Gated community with alarm system, cameras and perimeter sensors' },
				{ icon: faRoad, text: '5 minutes from the Sofia ring road' }
			],
			whyTitle: 'Why a house in Bistritsa?',
			whyParagraphs: [
				'Bistritsa sits at 897 m on the southern slopes of Vitosha — above the winter smog inversion that covers most of Sofia from November to March. On the worst days downtown, when the air quality index in the centre exceeds 120, the upper slopes stay in clean air and sunshine.',
				'The village offers what central Sofia cannot: rural quiet of around 30 dB instead of 55-75 in the city, mountain nature at your doorstep, and still only 5 minutes to the ring road. That is why the Bulgarian press calls Bistritsa "Sofia\'s Orange County" — and why it is home to the most expensive properties in the country.'
			],
			whyLink: 'Read how Bistritsa became Sofia\'s most expensive address',
			complexTitle: 'The gated community on Izgled Street',
			complexParagraphs: [
				'Vkushty is a gated community of four houses at the top of Izgled Street. Each house has 4 bedrooms and 4.5 bathrooms across three levels: garages and service rooms on level -1, a living floor with spacious living room, kitchen, office and fitness area, and a rest floor with a master bedroom and dressing room.',
				'The construction is new, with top-of-the-line suspended aluminium facades by Schüco, Germany. Each house has its own heated pool, rooftop photovoltaic system and heat pumps on a plot of 920-980 sq.m.'
			],
			galleryAlt: 'The Vkushty gated community of houses in Bistritsa',
			ctaTitle: 'See the houses',
			ctaText: 'Request the blueprints and a 3D tour, watch the videos, or book an on-site viewing.',
			ctaDownload: 'Blueprints & 3D tour',
			ctaVideos: 'Videos',
			contactTitle: 'Book a viewing',
			contactText:
				'Fill in the form and we will get back to you to arrange an on-site viewing in Bistritsa.'
		},
		bg: {
			title: 'Къщи в Бистрица, София — Затворен Комплекс Вкъщи | ул. Изглед',
			metaDescription:
				'Нови къщи за продажба в Бистрица, София: затворен комплекс от само 4 къщи на ул. Изглед. 640-780 кв.м, тавани 3,39 м, отопляем басейн, гараж за 4 коли, панорамна гледка над София.',
			h1: 'Къщи в Бистрица, София',
			subtitle: 'Бутиков затворен комплекс от само четири къщи на ул. Изглед',
			intro:
				'Ако търсите къща в Бистрица, Вкъщи е най-новият затворен комплекс в селото: четири съвременни къщи от 640-780 кв.м на върха на ул. Изглед, на 897 м надморска височина с 360° панорама над София. Ново строителство с окачени стъклени фасади Schüco, всяка къща върху собствен парцел от 920-980 кв.м.',
			heroAlt: 'Панорамна гледка над София от къщите Вкъщи в Бистрица',
			factsTitle: 'Къщите накратко',
			facts: [
				{ icon: faHouse, text: 'Само 4 къщи, 640-780 кв.м на три нива' },
				{ icon: faRulerVertical, text: 'Тавани 3,39 м светла височина' },
				{ icon: faPersonSwimming, text: 'Отопляем басейн във всяка къща' },
				{ icon: faWarehouse, text: 'Гараж за 4 коли' },
				{ icon: faElevator, text: 'Асансьор KONE' },
				{ icon: faSolarPanel, text: 'Фотоволтаици и термопомпи' },
				{ icon: faShieldHalved, text: 'Затворен комплекс със СОТ, камери и периметрови сензори' },
				{ icon: faRoad, text: '5 минути от Околовръстен път' }
			],
			whyTitle: 'Защо къща в Бистрица?',
			whyParagraphs: [
				'Бистрица е разположена на 897 м по южните склонове на Витоша — над зимната смог инверсия, която покрива по-голямата част от София от ноември до март. В най-лошите дни долу, когато индексът на качество на въздуха в центъра надхвърля 120, горните склонове остават на чист въздух и слънце.',
				'Селото предлага това, което централна София не може: селска тишина от около 30 dB вместо 55-75 в града, планинска природа на прага и въпреки това само 5 минути до Околовръстното. Затова българските медии наричат Бистрица „софийския Ориндж Каунти" — и затова тук се намират най-скъпите имоти в страната.'
			],
			whyLink: 'Прочетете как Бистрица стана най-скъпият адрес в София',
			complexTitle: 'Затвореният комплекс на ул. Изглед',
			complexParagraphs: [
				'Вкъщи е затворен комплекс от четири къщи на върха на ул. Изглед. Всяка къща има 4 спални и 4,5 бани на три нива: гаражи и сервизни помещения на ниво -1, дневен етаж с просторна дневна, кухня, офис и фитнес зона, и етаж за отдих с мастър спалня с голям дрешник.',
				'Строителството е ново, с първокласни окачени алуминиеви фасади от Schüco, Германия. Всяка къща разполага със собствен отопляем басейн, покривна фотоволтаична система и термопомпи, върху парцел от 920-980 кв.м.'
			],
			galleryAlt: 'Затвореният комплекс от къщи Вкъщи в Бистрица',
			ctaTitle: 'Разгледайте къщите',
			ctaText: 'Заявете чертежите и 3D обиколка, гледайте видеата или запишете оглед на място.',
			ctaDownload: 'Чертежи и 3D тур',
			ctaVideos: 'Видеа',
			contactTitle: 'Запишете оглед',
			contactText:
				'Попълнете формата и ще се свържем с вас, за да уговорим оглед на място в Бистрица.'
		}
	};

	const t = languageEn ? text.en : text.bg;
	const pagePath = `${languageEn ? '/en' : ''}/kashti-bistritsa`;
	const blogPath = `${languageEn ? '/en' : ''}/blog/how-bistritsa-became-sofias-most-expensive-address`;
	const downloadPath = `${languageEn ? '/en' : ''}/download`;
	const videosPath = `${languageEn ? '/en' : ''}/galeria`;

	return (
		<div className={styles.container}>
			<div id="snap-ready" style={{ display: 'none' }} />
			{/* defer={false} writes head tags synchronously — react-snap otherwise
			    races helmet's rAF-deferred update and can snapshot a bare <head>
			    (observed on /videos in the same build). */}
			<Helmet defer={false}>
				<html lang={languageEn ? 'en' : 'bg'} />
				<title>{t.title}</title>
				<meta name="description" content={t.metaDescription} />
				<link rel="canonical" href={`https://vkushty.com${pagePath}/`} />
				<meta property="og:title" content={t.title} />
				<meta property="og:description" content={t.metaDescription} />
				<meta property="og:url" content={`https://vkushty.com${pagePath}/`} />
				<meta property="og:type" content="website" />
				<meta property="og:image" content="https://vkushty.com/og-image.jpg" />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:image" content="https://vkushty.com/og-image.jpg" />
				<link rel="alternate" hreflang="bg" href="https://vkushty.com/kashti-bistritsa/" />
				<link rel="alternate" hreflang="en" href="https://vkushty.com/en/kashti-bistritsa/" />
				<link rel="alternate" hreflang="x-default" href="https://vkushty.com/kashti-bistritsa/" />
				<meta property="og:locale" content={languageEn ? "en_US" : "bg_BG"} />
				<meta property="og:locale:alternate" content={languageEn ? "bg_BG" : "en_US"} />
				<script type="application/ld+json">
					{JSON.stringify({
						'@context': 'https://schema.org',
						'@type': 'RealEstateListing',
						name: languageEn
							? 'Vkushty - Houses in a Gated Community in Bistritsa, Sofia'
							: 'Вкъщи - Къщи в затворен комплекс в Бистрица, София',
						description: t.metaDescription,
						url: `https://vkushty.com${pagePath}/`,
						image: 'https://vkushty.com/og-image.jpg',
						numberOfRooms: '4',
						numberOfBathroomsTotal: 5,
						floorSize: {
							'@type': 'QuantitativeValue',
							value: '640-780',
							unitCode: 'MTK'
						},
						address: {
							'@type': 'PostalAddress',
							streetAddress: 'Izgled Street',
							addressLocality: 'Bistritsa',
							addressRegion: 'Sofia',
							postalCode: '1444',
							addressCountry: 'BG'
						},
						geo: {
							'@type': 'GeoCoordinates',
							latitude: 42.6095,
							longitude: 23.3785,
							elevation: '897'
						},
						additionalProperty: [
							{ '@type': 'PropertyValue', name: 'Ceiling Height', value: '3.39m' },
							{ '@type': 'PropertyValue', name: 'Plot Size', value: '920-980 sq.m' },
							{ '@type': 'PropertyValue', name: 'Garage', value: '4-car' }
						]
					})}
				</script>
			</Helmet>

			<h1>{t.h1}</h1>
			<p className={styles.subtitle}>{t.subtitle}</p>

			<div className={styles.introSection}>
				<p>{t.intro}</p>
			</div>

			<img className={styles.heroImage} src={birdseye} alt={t.heroAlt} />

			<h2>{t.factsTitle}</h2>
			<div className={styles.featureGrid}>
				{t.facts.map((fact, index) => (
					<div key={index} className={styles.featureItem}>
						<div className={styles.featureIcon}>
							<FontAwesomeIcon icon={fact.icon} />
						</div>
						<p>{fact.text}</p>
					</div>
				))}
			</div>

			<h2>{t.whyTitle}</h2>
			{t.whyParagraphs.map((p, index) => (
				<p key={index} className={styles.sectionText}>
					{p}
				</p>
			))}
			<p className={styles.sectionText}>
				<Link className={styles.inlineLink} to={blogPath}>
					{t.whyLink}
				</Link>
			</p>

			<h2>{t.complexTitle}</h2>
			{t.complexParagraphs.map((p, index) => (
				<p key={index} className={styles.sectionText}>
					{p}
				</p>
			))}

			<img className={styles.heroImage} src={air1} alt={t.galleryAlt} loading="lazy" />

			<h2>{t.ctaTitle}</h2>
			<p className={styles.sectionText}>{t.ctaText}</p>
			<div className={styles.ctaRow}>
				<Link className={styles.ctaButton} to={downloadPath}>
					{t.ctaDownload}
				</Link>
				<Link className={styles.ctaButtonSecondary} to={videosPath}>
					{t.ctaVideos}
				</Link>
			</div>

			<h2>{t.contactTitle}</h2>
			<p className={styles.sectionText}>{t.contactText}</p>
			<div className={styles.contactWrapper}>
				<Contact languageEn={languageEn} />
			</div>
		</div>
	);
}

export default Bistritsa;
